import {html, css, CSSResultGroup, TemplateResult} from 'lit'
import {customElement, property} from 'lit/decorators.js'
import {BaseComponent} from './base-component.js'

@customElement('il-progress-bar')
export default class ProgressBarComponent extends BaseComponent {
  static override styles: CSSResultGroup = [
    BaseComponent.styles,
    css`
      .il-progress-bar {
        display: flex;
        gap: 16px;
        align-items: center;
      }

      .il-progress-bar .il-progress-bar__container {
        flex: 1 1 auto;
        position: relative;
        width: 100%;
        border-radius: 8px;
        height: 6px;
        overflow: hidden;
        background-color: var(--il-color-neutrals-100);
      }

      .il-progress-bar .il-progress-bar__bar {
        position: absolute;
        width: 0;
        height: 100%;
        background-color: var(--il-color-accent-200);
        transition: width .2s ease;
      }

      .il-progress-bar .il-progress-bar__steps {
        flex: 1 0 auto;
        font-size: var(--il-font-size-14);
        font-weight: var(--il-font-weight-normal);
        line-height: var(--il-font-line-height-150);
        letter-spacing: var(--il-font-letter-spacing-2);
        color: var(--il-color-neutrals-300);
      }
    `,
  ]

  @property({type: Number})
  step = 0

  @property({attribute: 'total-steps', type: Number})
  totalSteps = 0

  @property({attribute: 'steps-text', type: String})
  stepsText = 'Steps'

  @property({type: Number})
  progress = 0

  @property({attribute: 'show-steps', type: Boolean})
  showSteps = false

  @property({attribute: 'is-complete', type: Boolean})
  isComplete = false

  private stepsComponent (): TemplateResult {
    if (this.showSteps && this.step && this.totalSteps) {
      return html`
        <div
          class="il-progress-bar__steps"
          part="progress-bar-steps"
          data-test-iliad-progress-bar-steps
        >
          ${this.stepsText} ${this.step} / ${this.totalSteps}
        </div>
      `
    }

    return html``
  }

  override render (): TemplateResult {
    return html`
      <div
        class="${this.baseCSSClass} il-progress-bar"
        data-test-iliad-progress-bar-component
      >
        <slot></slot>
        <div class="il-progress-bar__container">
          <div
            class="il-progress-bar__bar"
            style="width: ${this.barWidth};"
            data-test-iliad-progress-bar
          ></div>
        </div>
        ${this.stepsComponent()}
      </div>
    `
  }

  get barWidth (): string {
    let progress = '0%'

    if (this.isComplete) {
      progress = '100%'
    } else if (this.progress) {
      progress = `${this.progress}%`
    } else if (this.step && this.totalSteps) {
      const _progress = ((this.step - 1) * 100) / this.totalSteps
      progress = `${_progress.toFixed(0)}%`
    }

    return progress
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'il-progress-bar': ProgressBarComponent
  }
}
